import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDate } from 'utils/formatDate';
import { useNotificationsRealtime } from 'context/notifications/NotificationsRealtimeContext';
import {
  getRequestChecklist,
  getDocumentFileUrl,
  getDocuments,
  decideDocument,
  reviewBatch,
  approveDocumentation,
  cancelProcess,
  getUnclassifiedDocuments,
  assignDocuments,
  uploadDocument,
  requestDocumentsAgain,
  manualCompleteItem,
  rejectDocumentItem,
  getOutboundMessages,
  resendOutboundMessage,
} from 'services/adminNegotiationService';
import TextArea from '../../components/TextArea/TextArea';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import AsyncStateView from '../../components/AsyncStateView/AsyncStateView';
import '../../styles/naEeppTableGrid.scss';

// Mapeo item.estado → color del Badge del design system (dos pasos, C2).
// aprobado = revisado y aprobado (success); recibido = esperando revisión (warning);
// expirado = venció la vigencia (error); rechazado = ítem rechazado por el supervisor o por
// la cascada de "Terminar proceso" (error — el Badge del DS usa `error` para rojo, NO `danger`).
const itemStatusColor = (estado) => {
  switch ((estado || '').toLowerCase()) {
    case 'aprobado':
      return 'success';
    case 'recibido':
      return 'warning';
    case 'expirado':
      return 'error';
    case 'rechazado':
      return 'error';
    case 'no_aplica':
    case 'pendiente':
    default:
      return 'light';
  }
};

// Badge de procedencia del documento (WhatsApp / Correo / Carga manual). Helper module-level
// (estable): recibe Badge y t por argumento para no sumar deps a los useMemo de columnas. Null si
// no hay canal. Un valor desconocido cae a WhatsApp, que es el default histórico de la columna.
const SOURCE_BADGE = {
  email: { key: 'cl_source_email', color: 'info' },
  manual: { key: 'cl_source_manual', color: 'light' },
  whatsapp: { key: 'cl_source_whatsapp', color: 'success' },
};
const sourceBadge = (source, Badge, t) => {
  if (!source) return null;
  const { key, color } = SOURCE_BADGE[source] ?? SOURCE_BADGE.whatsapp;
  return Badge ? (
    <Badge text={t(key)} color={color} size='sm' />
  ) : (
    <span style={{ fontSize: 11 }}>{t(key)}</span>
  );
};

const MSG_RULE_TYPES = new Set([
  'waiting',
  'approaching',
  'request',
  'reminder',
  'expired_rerequest',
  'user_rerequest',
  'cancellation',
  'documents_approved',
]);
const MSG_FLOW_TYPES = new Set(['doc_accepted', 'doc_discarded', 'close_case']);
const msgTypeLabel = (ruleType, t) => {
  if (MSG_RULE_TYPES.has(ruleType)) return t(`cfg_rule_type_${ruleType}`);
  if (MSG_FLOW_TYPES.has(ruleType)) return t(`msgs_type_${ruleType}`);
  return ruleType;
};

const msgStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'sent':
      return 'success';
    case 'failed':
      return 'error';
    case 'skipped':
      return 'warning';
    case 'stub':
    default:
      return 'light';
  }
};

/**
 * Detalle de un request (check aprobado) como CHECKLIST de revisión (C4), renderizado
 * como TABLA (Estado · Documento · Recibido · Acción). La única acción por fila es "Ver":
 * abre el documento en un modal de preview y es ahí —en el footer del preview— donde se
 * Aprueba o Rechaza el documento del ítem (obliga a verlo antes de decidir). Aprobar
 * acepta el documento (decideDocument); Rechazar pide una razón y reporta a EasyLawyer
 * (reviewBatch/legal-review) que re-solicita el documento al cliente. La acción global
 * "Aprobar documentación" vive en el footer del detalle, deshabilitada hasta que el
 * checklist esté completo (summary.isComplete); al dispararse manda el WhatsApp de
 * resolución al cliente (backend).
 *
 * Solo design system vía prop DesignSystemComponents + TextArea/ConfirmModal locales.
 */
export default function AdminNegotiationChecklistDetail({
  DesignSystemComponents,
  viabilityCheckId,
  t,
  onApproved,
  onChanged,
}) {
  const { Badge, Button, IconButton, Modal, Skeleton, Table, Dropdown } =
    DesignSystemComponents ?? {};
  const { subscribeToAdminNegotiation } = useNotificationsRealtime();

  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Preview del documento del ítem: url presignada + el ítem que se está viendo
  // (previewItem), sobre el que operan Aprobar/Rechazar del footer del preview.
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  // Modo rechazo dentro del preview (TextArea inline) + razón.
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Confirmación de "Aprobar documentación". `approved` es el flag local del approve
  // en esta sesión; el estado persistente sale de checklist.documentsApprovedAt (el DTO
  // sí lo expone), así que el badge "Aprobado" sobrevive al reabrir el request.
  const [approveOpen, setApproveOpen] = useState(false);
  const [approved, setApproved] = useState(false);
  // Estado async del aprobar (idle/loading/success/error) → body-swap (AsyncStateView).
  // `busy` se conserva (lo usan otros botones del footer); se setea junto a approveStatus.
  const [approveStatus, setApproveStatus] = useState('idle');
  const [approveError, setApproveError] = useState(null);

  // Modal "Terminar proceso" (G10): razón (code C2) + comentario (obligatorio si `other`) +
  // aviso de cascada. El estado terminal persiste en checklist.canceledAt (el DTO lo expone),
  // así que el badge "Terminado" y el read-only sobreviven al reabrir el request.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComment, setCancelComment] = useState('');
  const [canceling, setCanceling] = useState(false);
  // Estado async del terminar (idle/loading/success/error) → body-swap (AsyncStateView).
  // `canceling` se conserva (lo usan otros botones del footer); se setea junto a cancelStatus.
  const [cancelStatus, setCancelStatus] = useState('idle');
  const [cancelError, setCancelError] = useState(null);

  // Ítem con una acción en vuelo (deshabilita los botones del preview).
  const [busyItemId, setBusyItemId] = useState(null);
  // Aviso no-bloqueante { text, tone }: object + botón cerrar + auto-dismiss (patrón Viability).
  const [notice, setNotice] = useState(null);

  // ── Pool de documentos sin clasificar (needs_review sin ítem asignado del contacto) ──
  // Vista interna del modal: 'checklist' (revisión por ítem) | 'pool' (asignar/descartar).
  const [view, setView] = useState('checklist');
  const [pool, setPool] = useState([]);
  const [poolLoading, setPoolLoading] = useState(false);
  // Mapa { docId: itemId | '' } del destino elegido por doc (exclusión mutua entre filas).
  const [assignments, setAssignments] = useState({});
  const [poolBusy, setPoolBusy] = useState(false);
  const [poolFile, setPoolFile] = useState(null);
  const [poolUploading, setPoolUploading] = useState(false);
  // Preview del doc del pool: { url, doc } | null. SEPARADO del preview del checklist
  // (este es solo-lectura; no arrastra la semántica aprobar/rechazar del ítem).
  const [poolPreview, setPoolPreview] = useState(null);
  // Doc pendiente de confirmación de descarte (silencioso, sin WhatsApp) | null.
  const [discardTarget, setDiscardTarget] = useState(null);

  // ── Acciones por ítem del checklist (menú ⋮) ────────────────────────────────
  // Ítem en confirmación de descarte desde el ⋮: { item, mode: 'forget' | 'rerequest' } | null.
  const [discardItemTarget, setDiscardItemTarget] = useState(null);
  // Acción del ⋮ en vuelo (deshabilita el ⋮ y el botón confirmar del descarte).
  const [itemActionBusy, setItemActionBusy] = useState(false);
  // Modal "Solicitar de nuevo": { item, mode: 'rerequest' | 'discard_rerequest' } | null + motivo.
  // El motivo es obligatorio y viaja al cliente concatenado al texto de la regla user_rerequest.
  const [rerequestTarget, setRerequestTarget] = useState(null);
  const [rerequestReasonInput, setRerequestReasonInput] = useState('');
  const [rerequestBusy, setRerequestBusy] = useState(false);
  const [rerequestError, setRerequestError] = useState(null);
  // Modal "Rechazar documento": ítem en rechazo | null + motivo + toggle "volver a pedirlo" + estado.
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReasonInput, setRejectReasonInput] = useState('');
  const [rejectReRequest, setRejectReRequest] = useState(true);
  const [rejectBusy, setRejectBusy] = useState(false);

  // ── Reconciliación de duplicados (Fase 3): un ítem recibió >1 documento (el ganador
  // materializado + candidatos que llegaron por otro canal con archivo distinto). El modal
  // compara los archivos y el consultor elige cuál conservar; los demás se descartan. ──
  const [reconcileItem, setReconcileItem] = useState(null);
  // [{ id, source, fileName, isWinner, url }] — ganador (matchedDocumentId) + candidatos.
  const [reconcileDocs, setReconcileDocs] = useState([]);
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [rejectError, setRejectError] = useState(null);

  const [msgs, setMsgs] = useState([]);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const abortRef = useRef(null);
  const poolAbortRef = useRef(null);
  const msgsAbortRef = useRef(null);
  const debounceRef = useRef(null);
  const poolFileInputRef = useRef(null);

  // Refs al último dato cargado: fetchAll/fetchPool las leen para decidir el refetch
  // "silencioso" (ya hay data → no togglear loading) sin depender de checklist/pool en sus
  // useCallback (evita re-crearlos y re-disparar el efecto de montaje en cada cambio).
  const checklistRef = useRef(null);
  const poolRef = useRef([]);
  useEffect(() => {
    checklistRef.current = checklist;
  }, [checklist]);
  useEffect(() => {
    poolRef.current = pool;
  }, [pool]);

  const fetchAll = useCallback(
    async ({ silent } = {}) => {
      if (!viabilityCheckId) return;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Refetch silencioso: con data ya cargada NO togglear loading (la tabla conserva las
      // filas y se swapean al llegar la respuesta) ni limpiar el banner de error.
      const quiet = silent && !!checklistRef.current;
      if (!quiet) {
        setLoading(true);
        setError(null);
      }
      try {
        const data = await getRequestChecklist(
          viabilityCheckId,
          controller.signal
        );
        setChecklist(data);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [viabilityCheckId, t]
  );

  useEffect(() => {
    fetchAll();
    return () => abortRef.current?.abort();
  }, [fetchAll]);

  // Pool de documentos sin clasificar del contacto (mismo patrón abort que fetchAll).
  const fetchPool = useCallback(
    async ({ silent } = {}) => {
      if (!viabilityCheckId) return;
      if (poolAbortRef.current) poolAbortRef.current.abort();
      const controller = new AbortController();
      poolAbortRef.current = controller;
      // Refetch silencioso: con pool ya cargado NO togglear su loading (evita el flash de
      // skeleton de la tabla del pool en refrescos de fondo).
      const quiet = silent && poolRef.current.length > 0;
      if (!quiet) setPoolLoading(true);
      try {
        const data = await getUnclassifiedDocuments(
          viabilityCheckId,
          controller.signal
        );
        setPool(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError') return;
        // El pool es auxiliar: si falla lo dejamos vacío, sin pisar el banner de error
        // del checklist (la vista principal).
        setPool([]);
      } finally {
        if (!quiet) setPoolLoading(false);
      }
    },
    [viabilityCheckId]
  );

  useEffect(() => {
    fetchPool();
    return () => poolAbortRef.current?.abort();
  }, [fetchPool]);

  // Historial de mensajes outbound del request (mismo patrón abort que fetchAll). Los
  // errores de carga reusan el banner compartido con doc_error_generic.
  const fetchMessages = useCallback(async () => {
    if (!viabilityCheckId) return;
    if (msgsAbortRef.current) msgsAbortRef.current.abort();
    const controller = new AbortController();
    msgsAbortRef.current = controller;
    setMsgsLoading(true);
    setError(null);
    try {
      const data = await getOutboundMessages(
        viabilityCheckId,
        controller.signal
      );
      setMsgs(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('doc_error_generic')
      );
    } finally {
      setMsgsLoading(false);
    }
  }, [viabilityCheckId, t]);

  // Carga perezosa: el historial se pide al entrar a la vista "Mensajes"; se aborta al
  // salir o desmontar.
  useEffect(() => {
    if (view === 'messages') fetchMessages();
    return () => msgsAbortRef.current?.abort();
  }, [view, fetchMessages]);

  // Reenvío manual de una fila 'failed': fuerza el reintento del envío outbound (sin esperar
  // al drain automático). Busy por fila vía resendingId; al éxito refetcha para que la fila
  // pase a Enviado. Mismo patrón de error del archivo. (One-shot: sin abort propio, como el
  // resto de mutaciones; fetchMessages gestiona su msgsAbortRef.)
  const handleResendMessage = useCallback(
    async (msg) => {
      setError(null);
      setResendingId(msg.id);
      try {
        await resendOutboundMessage(msg.id);
        await fetchMessages();
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      } finally {
        setResendingId(null);
      }
    },
    [fetchMessages, t]
  );

  // Al cambiar de request: volver a la vista checklist y limpiar asignaciones en curso.
  useEffect(() => {
    setView('checklist');
    setAssignments({});
  }, [viabilityCheckId]);

  // El aviso (notice) se auto-cierra a los 6s; igual se puede cerrar a mano (botón ×).
  useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(id);
  }, [notice]);

  // Realtime: nos suscribimos al grupo global de NA; cuando el backend emite
  // admin_negotiation_updated para ESTE check (documento clasificado, docs listos,
  // documentación aprobada) refetchamos el detalle. Debounce 400ms para coalescer
  // ráfagas de eventos (mismo patrón que EscriturasList).
  useEffect(() => {
    const unsubscribe = subscribeToAdminNegotiation((payload) => {
      // Filtrar por check: el detalle solo reacciona a eventos de SU request.
      if (payload && Number(payload.checkId) !== Number(viabilityCheckId))
        return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchAll({ silent: true });
        fetchPool({ silent: true });
      }, 400);
    });

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      unsubscribe();
    };
  }, [subscribeToAdminNegotiation, viabilityCheckId, fetchAll, fetchPool]);

  // Ver/descargar: resuelve el doc recibido por su path; el backend de file-url usa el
  // id del documento, así que ubicamos el doc del contacto cuyo path matchea el item.
  const findDocForItem = useCallback(
    async (item) => {
      if (!item.fileS3Path || !checklist?.contactIdentifier) return null;
      const controller = new AbortController();
      const docs = await getDocuments(
        { contactId: checklist.contactIdentifier },
        controller.signal
      );
      return (
        (Array.isArray(docs) ? docs : []).find(
          (d) => d.s3Path === item.fileS3Path
        ) || null
      );
    },
    [checklist]
  );

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewItem(null);
    setPreviewUrl(null);
    setRejecting(false);
    setRejectReason('');
  }, []);

  // Ver: abre el documento del ítem en el modal de preview (donde se aprueba/rechaza).
  const handleView = useCallback(
    async (item) => {
      setError(null);
      try {
        const doc = await findDocForItem(item);
        if (!doc) {
          setError(t('doc_error_generic'));
          return;
        }
        const url = await getDocumentFileUrl(doc.id, { inline: true });
        if (!url) return;
        setPreviewUrl(url);
        setPreviewItem(item);
        setRejecting(false);
        setRejectReason('');
        setPreviewOpen(true);
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      }
    },
    [findDocForItem, t]
  );

  const handleDownload = useCallback(
    async (item) => {
      setError(null);
      try {
        const doc = await findDocForItem(item);
        if (!doc) {
          setError(t('doc_error_generic'));
          return;
        }
        const url = await getDocumentFileUrl(doc.id);
        if (!url) return;
        const a = document.createElement('a');
        a.href = url;
        a.download = item.fileName || `doc-${doc.id}`;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      }
    },
    [findDocForItem, t]
  );

  // Aprobar ítem (desde el preview): resuelve el doc del ítem y lo acepta asignándolo al
  // ítem del checklist. Cierra el preview y refetcha el detalle para reflejar el estado.
  const handleApproveItem = useCallback(
    async (item) => {
      setError(null);
      setBusyItemId(item.id);
      try {
        const doc = await findDocForItem(item);
        if (!doc) {
          setError(t('doc_error_generic'));
          return;
        }
        await decideDocument(doc.id, 'accept', item.id);
        closePreview();
        await fetchAll({ silent: true });
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      } finally {
        setBusyItemId(null);
      }
    },
    [findDocForItem, fetchAll, closePreview, t]
  );

  // ── Reconciliación de duplicados (Fase 3) ──────────────────────────────────
  // Abre el modal comparativo: arma la lista [ganador, ...candidatos] y resuelve las URLs de
  // preview (inline, presigned) en paralelo.
  const handleReconcile = useCallback(
    async (item) => {
      setError(null);
      try {
        const entries = [];
        if (item.matchedDocumentId) {
          entries.push({
            id: item.matchedDocumentId,
            source: item.receivedChannel,
            fileName: item.fileName,
            isWinner: true,
          });
        }
        (item.candidates || []).forEach((c) => {
          entries.push({
            id: c.id,
            source: c.source,
            fileName: c.fileName,
            isWinner: false,
          });
        });
        const withUrls = await Promise.all(
          entries.map(async (e) => {
            try {
              return {
                ...e,
                url: await getDocumentFileUrl(e.id, { inline: true }),
              };
            } catch {
              return { ...e, url: null };
            }
          })
        );
        setReconcileItem(item);
        setReconcileDocs(withUrls);
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      }
    },
    [t]
  );

  const closeReconcile = useCallback(() => {
    setReconcileItem(null);
    setReconcileDocs([]);
    setReconcileBusy(false);
  }, []);

  // Conservar el elegido: lo acepta en el ítem (reemplaza el archivo, incluso si el ítem ya
  // estaba recibido) y descarta los demás (silencioso, sin WhatsApp). Nunca re-pide ni cancela.
  const handleReconcileKeep = useCallback(
    async (chosenId) => {
      if (!reconcileItem || reconcileBusy) return;
      setReconcileBusy(true);
      setError(null);
      try {
        await decideDocument(chosenId, 'accept', reconcileItem.id);
        const others = reconcileDocs.filter((d) => d.id !== chosenId);
        // Secuencial: el accept ya movió el ítem a aprobado; los discard no lo revierten.
        // eslint-disable-next-line no-await-in-loop
        for (const o of others)
          await decideDocument(o.id, 'discard', null, false);
        closeReconcile();
        await fetchAll({ silent: true });
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
        setReconcileBusy(false);
      }
    },
    [reconcileItem, reconcileDocs, reconcileBusy, fetchAll, closeReconcile, t]
  );

  // Rechazar ítem (desde el preview): reporta a EasyLawyer (legal-review) el ítem
  // rechazado + motivo; el backend re-solicita el documento al cliente.
  const handleRejectItem = useCallback(
    async (item) => {
      setError(null);
      setBusyItemId(item.id);
      try {
        const updated = await reviewBatch(viabilityCheckId, [
          {
            checklistItemId: item.id,
            approved: false,
            reason: rejectReason.trim() || undefined,
          },
        ]);
        closePreview();
        if (updated) setChecklist(updated);
        else await fetchAll({ silent: true });
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      } finally {
        setBusyItemId(null);
      }
    },
    [viabilityCheckId, rejectReason, fetchAll, closePreview, t]
  );

  // "Aprobar documentación": exige checklist completo → el backend manda el WhatsApp
  // de resolución al cliente. 409 si quedan obligatorios pendientes.
  // clientNotificationStatus === 'failed' → la aprobación SÍ se aplicó pero el WhatsApp no
  // salió (queda encolado): estado warning, sin auto-cierre. sent/skipped/null → success.
  const handleApproveDocumentation = useCallback(async () => {
    setApproveStatus('loading');
    setBusy(true);
    setApproveError(null);
    setError(null);
    try {
      const updated = await approveDocumentation(viabilityCheckId);
      if (updated) setChecklist(updated);
      setApproved(true);
      setApproveStatus(
        updated?.clientNotificationStatus === 'failed' ? 'warning' : 'success'
      );
    } catch (err) {
      setApproveError(
        err.response?.data?.message || err.message || t('doc_error_generic')
      );
      setApproveStatus('error');
    } finally {
      setBusy(false);
    }
  }, [viabilityCheckId, t]);

  // "Terminar proceso": estado terminal `canceled` con razón KPI-able. El back cascada los ítems
  // sin disponer → rechazado, cierra la tarea 2 y (si hay regla activa) manda el WhatsApp de
  // término. La validación de front (comentario obligatorio con `other`) espeja la del back; el
  // 409 (proceso no en recolección / ya aprobado) cae al banner de error. Refresca el checklist:
  // canceledAt deja la vista read-only con el badge "Terminado". Avisa al padre (onChanged) para
  // que la tabla de Solicitudes refleje el nuevo estado.
  const handleCancelProcess = useCallback(async () => {
    setCancelStatus('loading');
    setCanceling(true);
    setCancelError(null);
    setError(null);
    try {
      const updated = await cancelProcess(
        viabilityCheckId,
        cancelReason,
        cancelComment.trim() || null
      );
      if (updated) setChecklist(updated);
      // Igual que el approve: si el WhatsApp de término no salió (clientNotificationStatus
      // 'failed') el término YA quedó aplicado → warning sin auto-cierre.
      setCancelStatus(
        updated?.clientNotificationStatus === 'failed' ? 'warning' : 'success'
      );
    } catch (err) {
      setCancelError(
        err.response?.data?.message || err.message || t('doc_error_generic')
      );
      setCancelStatus('error');
    } finally {
      setCanceling(false);
    }
  }, [viabilityCheckId, cancelReason, cancelComment, t]);

  // Pool — Ver: abre el doc del pool en un modal de preview PROPIO (solo lectura).
  const handlePoolView = useCallback(
    async (doc) => {
      setError(null);
      try {
        const url = await getDocumentFileUrl(doc.id, { inline: true });
        if (!url) return;
        setPoolPreview({ url, doc });
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      }
    },
    [t]
  );

  // Pool — Descargar (desde el preview del pool).
  const handlePoolDownload = useCallback(
    async (doc) => {
      setError(null);
      try {
        const url = await getDocumentFileUrl(doc.id);
        if (!url) return;
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.fileName || `doc-${doc.id}`;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      }
    },
    [t]
  );

  // Pool — Guardar asignaciones: marca los ítems recibidos (los docs siguen needs_review,
  // se aprueban después vía Ver → Aprobar). Pinta desde la respuesta (sin refetch); los
  // fallos blandos por asignación se muestran como error joineado.
  const handleSaveAssignments = useCallback(async () => {
    const entries = Object.entries(assignments)
      .filter(([, itemId]) => itemId !== '' && itemId != null)
      .map(([docId, itemId]) => ({
        documentId: Number(docId),
        checklistItemId: Number(itemId),
      }));
    if (entries.length === 0) return;
    setPoolBusy(true);
    setError(null);
    try {
      const res = await assignDocuments(viabilityCheckId, entries);
      if (res?.checklist) setChecklist(res.checklist);
      setPool(Array.isArray(res?.unclassified) ? res.unclassified : []);
      setAssignments({});
      // Avisar al padre para que refresque el contador de sin-clasificar de la tabla de
      // Solicitudes de forma determinística (el realtime del assign es best-effort).
      onChanged?.();
      // El resultado ya es visible en la tabla (los ítems pasan a "recibido"); solo los
      // fallos blandos por asignación se reportan como error.
      if (res?.failed?.length) {
        setError(res.failed.map((f) => f.message).join('; '));
      }
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || t('doc_error_generic')
      );
    } finally {
      setPoolBusy(false);
    }
  }, [assignments, viabilityCheckId, onChanged, t]);

  // Pool — Subir documento: sube para este contacto/check (se clasifica normal; si no
  // matchea cae al pool). Refresca pool + checklist.
  const handlePoolUpload = useCallback(async () => {
    if (!poolFile || !checklist?.contactIdentifier) return;
    setPoolUploading(true);
    setError(null);
    try {
      await uploadDocument({
        contactId: checklist.contactIdentifier,
        viabilityCheckId,
        file: poolFile,
      });
      setPoolFile(null);
      if (poolFileInputRef.current) poolFileInputRef.current.value = '';
      await Promise.all([
        fetchPool({ silent: true }),
        fetchAll({ silent: true }),
      ]);
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || t('doc_error_generic')
      );
    } finally {
      setPoolUploading(false);
    }
  }, [poolFile, checklist, viabilityCheckId, fetchPool, fetchAll, t]);

  // Pool — Descartar silencioso: descarta el doc sobrante SIN WhatsApp al cliente.
  const handleDiscard = useCallback(async () => {
    if (!discardTarget) return;
    setPoolBusy(true);
    setError(null);
    try {
      await decideDocument(discardTarget.id, 'discard', null, false);
      setDiscardTarget(null);
      await fetchPool({ silent: true });
      // El descarte silencioso no emite realtime: avisar al padre para que el badge de
      // documentos sin clasificar de la tabla de Solicitudes baje en vivo.
      onChanged?.();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || t('doc_error_generic')
      );
    } finally {
      setPoolBusy(false);
    }
  }, [discardTarget, fetchPool, onChanged, t]);

  // ⋮ Re-pedir (pendiente): dispara YA el nudge del set pendiente (endpoint por-lista; el
  // targeting por-ítem se difiere), con el motivo que escribió el supervisor. Toast honesto
  // según res.requestSent: success si el outbound quedó "sent", warning si no se envió (sin
  // regla activa o EasyLawyer falló).
  const handleReRequest = useCallback(
    async (item, reason) => {
      setError(null);
      setItemActionBusy(true);
      try {
        const res = await requestDocumentsAgain(
          viabilityCheckId,
          [item.documento],
          reason
        );
        if (res?.checklist) setChecklist(res.checklist);
        else await fetchAll({ silent: true });
        onChanged?.();
        setNotice(
          res?.requestSent
            ? { text: t('cl_item_rerequest_success'), tone: 'success' }
            : { text: t('cl_item_rerequest_failed'), tone: 'warning' }
        );
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
        throw err;
      } finally {
        setItemActionBusy(false);
      }
    },
    [viabilityCheckId, fetchAll, onChanged, t]
  );

  // ⋮ Marcar como entregado (pendiente → aprobado): el cliente ya lo entregó antes. No
  // notifica al cliente; puede destrabar "Aprobar documentación" si era el último obligatorio.
  const handleMarkDelivered = useCallback(
    async (item) => {
      setError(null);
      setItemActionBusy(true);
      try {
        const updated = await manualCompleteItem(
          viabilityCheckId,
          item.id,
          'delivered'
        );
        if (updated) setChecklist(updated);
        else await fetchAll({ silent: true });
        onChanged?.();
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      } finally {
        setItemActionBusy(false);
      }
    },
    [viabilityCheckId, fetchAll, onChanged, t]
  );

  // ⋮ Marcar como no aplica (pendiente → no_aplica): ya no se necesita. No notifica al
  // cliente; también completa el obligatorio para "Aprobar documentación".
  const handleMarkNa = useCallback(
    async (item) => {
      setError(null);
      setItemActionBusy(true);
      try {
        const updated = await manualCompleteItem(
          viabilityCheckId,
          item.id,
          'not_applicable'
        );
        if (updated) setChecklist(updated);
        else await fetchAll({ silent: true });
        onChanged?.();
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      } finally {
        setItemActionBusy(false);
      }
    },
    [viabilityCheckId, fetchAll, onChanged, t]
  );

  // ⋮ Rechazar → abre el modal (documento + motivo + toggle "volver a pedirlo"). Al confirmar el
  // ítem vuelve a PENDIENTE (NO bloquea/cancela el proceso) y, con el toggle activo, se le manda un
  // WhatsApp al cliente con el motivo para que reenvíe el documento (backend RejectDocumentAsync).
  const handleConfirmReject = useCallback(async () => {
    if (!rejectTarget) return;
    const reason = rejectReasonInput.trim();
    if (reason.length === 0) return;
    setRejectError(null);
    setRejectBusy(true);
    try {
      const updated = await rejectDocumentItem(
        viabilityCheckId,
        rejectTarget.id,
        { reason, reRequest: rejectReRequest }
      );
      if (updated) setChecklist(updated);
      else await fetchAll({ silent: true });
      onChanged?.();
      setRejectTarget(null);
      setRejectReasonInput('');
      setRejectReRequest(true);
      setNotice({
        text: rejectReRequest ? t('cl_reject_ok_rerequest') : t('cl_reject_ok'),
        tone: 'success',
      });
    } catch (err) {
      setRejectError(
        err.response?.data?.message || err.message || t('doc_error_generic')
      );
    } finally {
      setRejectBusy(false);
    }
  }, [
    rejectTarget,
    rejectReasonInput,
    rejectReRequest,
    viabilityCheckId,
    fetchAll,
    onChanged,
    t,
  ]);

  // ⋮ Descartar y olvidar (recibido): resuelve el doc del ítem y lo descarta SILENCIOSO
  // (notifyClient:false). El back revierte el ítem recibido→pendiente. Sin WhatsApp.
  const handleDiscardForget = useCallback(
    async (item) => {
      setError(null);
      setItemActionBusy(true);
      try {
        const doc = await findDocForItem(item);
        if (!doc) {
          setError(t('doc_error_generic'));
          return;
        }
        await decideDocument(doc.id, 'discard', null, false);
        setDiscardItemTarget(null);
        await fetchAll({ silent: true });
        onChanged?.();
        setNotice({
          text: t('cl_item_discard_forget_success'),
          tone: 'success',
        });
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
      } finally {
        setItemActionBusy(false);
      }
    },
    [findDocForItem, fetchAll, onChanged, t]
  );

  // ⋮ Descartar y re-pedir (recibido): descarta SILENCIOSO (el back revierte a pendiente) y
  // luego dispara el nudge → un único WhatsApp. Toast honesto: si el descarte fue OK pero
  // requestSent===false, el doc queda descartado igual y se muestra el warning (no success).
  const handleDiscardReRequest = useCallback(
    async (item, reason) => {
      setError(null);
      setItemActionBusy(true);
      try {
        const doc = await findDocForItem(item);
        if (!doc) {
          setError(t('doc_error_generic'));
          return;
        }
        await decideDocument(doc.id, 'discard', null, false);
        const res = await requestDocumentsAgain(
          viabilityCheckId,
          [item.documento],
          reason
        );
        if (res?.checklist) setChecklist(res.checklist);
        else await fetchAll({ silent: true });
        onChanged?.();
        setNotice(
          res?.requestSent
            ? { text: t('cl_item_discard_rerequest_success'), tone: 'success' }
            : { text: t('cl_item_rerequest_failed'), tone: 'warning' }
        );
      } catch (err) {
        setError(
          err.response?.data?.message || err.message || t('doc_error_generic')
        );
        throw err;
      } finally {
        setItemActionBusy(false);
      }
    },
    [findDocForItem, viabilityCheckId, fetchAll, onChanged, t]
  );

  // Confirmación del modal "Solicitar de nuevo": el motivo es obligatorio y se despacha al
  // handler que corresponda según el estado del ítem. El modal solo se cierra si salió bien.
  const handleConfirmReRequest = useCallback(async () => {
    if (!rerequestTarget) return;
    const reason = rerequestReasonInput.trim();
    if (reason.length === 0) return;
    setRerequestError(null);
    setRerequestBusy(true);
    try {
      if (rerequestTarget.mode === 'discard_rerequest')
        await handleDiscardReRequest(rerequestTarget.item, reason);
      else await handleReRequest(rerequestTarget.item, reason);
      setRerequestTarget(null);
      setRerequestReasonInput('');
    } catch (err) {
      setRerequestError(
        err.response?.data?.message || err.message || t('doc_error_generic')
      );
    } finally {
      setRerequestBusy(false);
    }
  }, [
    rerequestTarget,
    rerequestReasonInput,
    handleReRequest,
    handleDiscardReRequest,
    t,
  ]);

  // Despacho del ⋮: mark_delivered/mark_na disparan al vuelo; re-pedir y rechazar piden motivo;
  // el descarte silencioso abre su ConfirmModal.
  const handleItemMenuSelect = useCallback(
    (item, value) => {
      switch (value) {
        case 'rerequest':
          setRerequestReasonInput('');
          setRerequestError(null);
          return setRerequestTarget({ item, mode: 'rerequest' });
        case 'mark_delivered':
          return handleMarkDelivered(item);
        case 'mark_na':
          return handleMarkNa(item);
        case 'reject':
          return setRejectTarget(item);
        case 'discard_forget':
          return setDiscardItemTarget({ item, mode: 'forget' });
        case 'discard_rerequest':
          setRerequestReasonInput('');
          setRerequestError(null);
          return setRerequestTarget({ item, mode: 'discard_rerequest' });
        default:
          return undefined;
      }
    },
    [handleMarkDelivered, handleMarkNa]
  );

  // Proceso terminado (canceled): el DTO expone canceledAt (espejo de documentsApprovedAt).
  // Deja toda la vista read-only (sin ⋮, sin aprobar/rechazar en el preview, sin terminar).
  const isCanceled = !!checklist?.canceledAt;

  // Gating de acciones: el modal solo es OPERABLE mientras el proceso está en recolección
  // (check `confirmed`, ni terminado ni con la documentación aprobada) — el back bloquea
  // igual las mutaciones fuera de ese estado. Con `actionable === false` la vista es de
  // solo lectura: sin "Terminar proceso", sin "Aprobar documentación", sin menú ⋮ por ítem
  // y sin Aprobar/Rechazar en el footer del preview. "Ver" y "Mensajes" quedan SIEMPRE
  // (el ojo se abre en todas las filas para consultar documentos e historial).
  // Generaliza el read-only que antes derivaba solo de canceledAt.
  const actionable =
    checklist?.status === 'confirmed' &&
    !checklist?.canceledAt &&
    !checklist?.documentsApprovedAt;

  // Columnas de la tabla del checklist: Estado · Documento · Recibido · Acción.
  const columns = useMemo(
    () => [
      {
        accessor: 'estado',
        label: t('cl_col_status'),
        render: (item) =>
          Badge ? (
            <Badge
              text={t(`cl_estado_${item.estado || 'pendiente'}`)}
              color={itemStatusColor(item.estado)}
              size='sm'
            />
          ) : (
            <span>{t(`cl_estado_${item.estado || 'pendiente'}`)}</span>
          ),
      },
      {
        accessor: 'documento',
        label: t('cl_col_document'),
        render: (item) => (
          // flex-wrap + minWidth:0 en el nombre: con un documento de nombre largo los badges
          // bajan de línea en vez de recortarse.
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            <span
              style={{
                fontSize: 14,
                color: '#0F172A',
                fontWeight: 500,
                minWidth: 0,
              }}
            >
              {item.documento}
            </span>
            {item.receivedChannel && (
              <span style={{ flexShrink: 0 }}>
                {sourceBadge(item.receivedChannel, Badge, t)}
              </span>
            )}
            {item.candidates?.length > 0 && (
              <button
                type='button'
                onClick={() => handleReconcile(item)}
                title={t('cl_reconcile_title')}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                {Badge ? (
                  <Badge
                    text={`${1 + item.candidates.length} ${t('cl_reconcile_docs')}`}
                    color='warning'
                    size='sm'
                  />
                ) : (
                  <span style={{ fontSize: 11 }}>
                    {1 + item.candidates.length} {t('cl_reconcile_docs')}
                  </span>
                )}
              </button>
            )}
          </div>
        ),
      },
      {
        accessor: 'receivedAt',
        label: t('cl_col_received'),
        render: (item) => (item.receivedAt ? formatDate(item.receivedAt) : '—'),
      },
      {
        accessor: 'actions',
        label: t('cl_col_actions'),
        render: (item) => {
          // "Ver" para cualquier ítem que conserve documento (recibido/aprobado/expirado);
          // aprobar/rechazar se resuelve dentro del preview (solo si está `recibido`).
          const hasDoc =
            ['recibido', 'aprobado', 'expirado'].includes(item.estado) &&
            !!item.fileS3Path;
          // Menú ⋮ gateado por estado (spec del plan): recibido → descartes; pendiente →
          // re-pedir / marcar entregado / marcar no aplica; expirado → solo rechazar;
          // "Rechazar" (mode 'rejected') disponible en pendiente/recibido/expirado.
          // aprobado/no_aplica/rechazado → sin ⋮. Proceso fuera de recolección
          // (actionable === false) → read-only.
          let menuOptions = [];
          if (actionable && item.estado === 'recibido') {
            // "Solicitar de nuevo" arriba de "Rechazar y descartar" (spec del plan).
            // El color rojo por-opción del ⋮ NO lo soporta el Dropdown del DS (delega
            // en react-select vía ComboBox y no lee `color` por opción) → queda default.
            menuOptions = [
              {
                label: t('cl_item_discard_rerequest'),
                value: 'discard_rerequest',
              },
              { label: t('cl_item_discard_forget'), value: 'discard_forget' },
              { label: t('cl_item_reject'), value: 'reject' },
            ];
          } else if (actionable && item.estado === 'pendiente') {
            menuOptions = [
              { label: t('cl_item_rerequest'), value: 'rerequest' },
              { label: t('cl_item_mark_delivered'), value: 'mark_delivered' },
              { label: t('cl_item_mark_na'), value: 'mark_na' },
              { label: t('cl_item_reject'), value: 'reject' },
            ];
          } else if (actionable && item.estado === 'expirado') {
            menuOptions = [{ label: t('cl_item_reject'), value: 'reject' }];
          }
          const showView = IconButton && hasDoc;
          const showMenu = Dropdown && menuOptions.length > 0;
          if (!showView && !showMenu) return <span>—</span>;
          return (
            <div className='d-flex gap-1 flex-wrap align-items-center'>
              {showView && (
                <IconButton
                  icon='eye'
                  size='sm'
                  stroke='var(--primary-500)'
                  ariaLabel={t('doc_view')}
                  onClick={() => handleView(item)}
                />
              )}
              {showMenu && (
                <Dropdown
                  variant='icon'
                  icon='threeDotsVertical'
                  ariaLabel={t('cl_item_menu')}
                  options={menuOptions}
                  onSelect={(opt) => handleItemMenuSelect(item, opt?.value)}
                  disabled={itemActionBusy}
                />
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      Badge,
      IconButton,
      Dropdown,
      handleView,
      handleReconcile,
      handleItemMenuSelect,
      itemActionBusy,
      actionable,
    ]
  );

  // Ítems del checklist que aún pueden recibir un doc del pool (destino de la asignación).
  const pendingItems = useMemo(
    () => (checklist?.items || []).filter((i) => i.estado === 'pendiente'),
    [checklist]
  );

  // Columnas de la tabla del pool: Documento · Recibido · Asignar a · Acción.
  const poolColumns = useMemo(
    () => [
      {
        accessor: 'fileName',
        label: t('cl_pool_col_file'),
        render: (doc) => (
          <div>
            <span style={{ fontSize: 14, color: '#0F172A', fontWeight: 500 }}>
              {doc.fileName || '—'}
            </span>
            {doc.classifiedType && (
              <p
                className='mb-0'
                style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}
              >
                {doc.classifiedType}
              </p>
            )}
            {doc.source && (
              <div className='mt-1'>{sourceBadge(doc.source, Badge, t)}</div>
            )}
          </div>
        ),
      },
      {
        accessor: 'createdAt',
        label: t('cl_pool_col_received'),
        render: (doc) => (doc.createdAt ? formatDate(doc.createdAt) : '—'),
      },
      {
        accessor: 'assign',
        label: t('cl_pool_col_assign'),
        // Exclusión mutua: un ítem elegido en una fila desaparece de las demás. Si a la
        // fila no le queda ningún ítem elegible y no tiene selección → Dropdown bloqueado.
        render: (doc) => {
          if (!Dropdown) return null;
          const own = assignments[doc.id] ?? '';
          const taken = new Set(
            Object.entries(assignments)
              .filter(([docId]) => Number(docId) !== doc.id)
              .map(([, itemId]) => itemId)
              .filter((itemId) => itemId !== '' && itemId != null)
          );
          const available = pendingItems.filter(
            (i) => !taken.has(i.id) || i.id === own
          );
          const noSlots = available.length === 0 && own === '';
          const options = [
            {
              label: noSlots
                ? t('cl_pool_no_slots')
                : t('cl_pool_assign_placeholder'),
              value: '',
            },
            ...available.map((i) => ({ label: i.documento, value: i.id })),
          ];
          const selected = options.find((o) => o.value === own) ?? options[0];
          return (
            <Dropdown
              options={options}
              value={selected}
              onChange={(opt) =>
                setAssignments((prev) => ({
                  ...prev,
                  [doc.id]: opt?.value ?? '',
                }))
              }
              disabled={noSlots}
              width='100%'
            />
          );
        },
      },
      {
        accessor: 'actions',
        label: t('cl_col_actions'),
        // IconButtons inline (ver + descartar): ocupan menos ancho que los Button de texto,
        // así dejan de apilarse. El descarte mantiene el ConfirmModal (setDiscardTarget).
        render: (doc) => (
          <div className='d-flex gap-1 align-items-center'>
            {IconButton && (
              <IconButton
                icon='eye'
                size='sm'
                stroke='var(--primary-500)'
                ariaLabel={t('doc_view')}
                onClick={() => handlePoolView(doc)}
              />
            )}
            {IconButton && (
              <IconButton
                icon='trash'
                size='sm'
                iconStroke='var(--error-500)'
                ariaLabel={t('cl_pool_discard')}
                onClick={() => setDiscardTarget(doc)}
              />
            )}
          </div>
        ),
      },
    ],
    [t, Badge, Dropdown, IconButton, pendingItems, assignments, handlePoolView]
  );

  const msgColumns = useMemo(
    () => [
      {
        accessor: 'createAt',
        label: t('msgs_col_date'),
        render: (msg) => (msg.createAt ? formatDate(msg.createAt) : '—'),
      },
      {
        accessor: 'ruleType',
        label: t('msgs_col_type'),
        render: (msg) => (
          <span style={{ fontSize: 14, color: '#0F172A', fontWeight: 500 }}>
            {msg.ruleType ? msgTypeLabel(msg.ruleType, t) : '—'}
          </span>
        ),
      },
      {
        accessor: 'status',
        label: t('msgs_col_status'),
        render: (msg) =>
          Badge ? (
            <Badge
              text={t(`msgs_status_${msg.status || 'stub'}`)}
              color={msgStatusColor(msg.status)}
              size='sm'
            />
          ) : (
            <span>{t(`msgs_status_${msg.status || 'stub'}`)}</span>
          ),
      },
      {
        accessor: 'detail',
        label: t('msgs_col_detail'),
        // Detalle = statusCode (incl. 0 = excepción/config faltante) + errorDetail cuando
        // los hay; solo failed trae errorDetail. '—' si no hay ninguno.
        render: (msg) => {
          const code = msg.statusCode != null ? String(msg.statusCode) : null;
          const detail = msg.errorDetail || null;
          if (!code && !detail) return '—';
          return (
            <span style={{ fontSize: 13, color: '#475569' }}>
              {[code, detail].filter(Boolean).join(' · ')}
            </span>
          );
        },
      },
      {
        accessor: 'resend',
        label: '',
        // Reenviar: solo en filas 'failed' (fuerza el reintento sin esperar al drain). El
        // resto de filas → celda vacía. Deshabilitado mientras esta fila está reenviando.
        render: (msg) => {
          if (msg.status !== 'failed' || !IconButton) return <span />;
          return (
            <IconButton
              icon='arrowCounterclockwise'
              size='sm'
              stroke='var(--primary-500)'
              ariaLabel={t('msgs_resend')}
              onClick={() => handleResendMessage(msg)}
              disabled={resendingId === msg.id}
            />
          );
        },
      },
    ],
    [t, Badge, IconButton, resendingId, handleResendMessage]
  );

  if (loading) {
    return (
      <div className='d-flex flex-column gap-2'>
        {Skeleton ? (
          [0, 1, 2].map((i) => (
            <Skeleton key={i} height='2.5rem' width='100%' />
          ))
        ) : (
          <p className='font-light'>{t('doc_loading')}</p>
        )}
      </div>
    );
  }

  if (!checklist) {
    return <p className='caption font-light'>{t('doc_empty')}</p>;
  }

  const items = checklist.items || [];
  const summary = checklist.summary || {};
  const isApproved = approved || !!checklist?.documentsApprovedAt;
  const canReviewPreview =
    previewItem &&
    previewItem.estado === 'recibido' &&
    !!previewItem.fileS3Path &&
    actionable;
  const previewBusy = previewItem && busyItemId === previewItem.id;
  const hasAssignments = Object.values(assignments).some(
    (v) => v !== '' && v != null
  );

  // "Terminar proceso" solo aplica a un proceso confirmado que sigue en recolección: el
  // back 409-guardea los demás estados, así que reusamos el mismo gating del modal.
  const canTerminate = actionable;

  // Razones de término (codes C2 almacenados en inglés; labels vía i18n). El Dropdown arranca
  // sin selección (value null → placeholder): la razón es KPI-able, no se defaultea.
  const cancelReasonOptions = [
    { label: t('term_reason_client_abandoned'), value: 'client_abandoned' },
    { label: t('term_reason_no_response'), value: 'no_response' },
    { label: t('term_reason_service_change'), value: 'service_change' },
    { label: t('term_reason_non_viable'), value: 'non_viable' },
    { label: t('term_reason_other'), value: 'other' },
  ];
  // Ítems que la cascada dejará en rechazado al terminar (los que aún no están dispuestos).
  const cascadeItems = items.filter((i) =>
    ['pendiente', 'recibido', 'expirado'].includes(i.estado)
  );
  // Comentario obligatorio cuando la razón es `other` (espejo de la validación del back).
  const canConfirmCancel =
    !!cancelReason &&
    (cancelReason !== 'other' || cancelComment.trim().length > 0);

  return (
    <div>
      {/* Avisos: separación (mb-3) para NO tapar el header/acciones del detalle. */}
      {error && (
        <div className='alert alert-danger py-2 mb-3' style={{ fontSize: 13 }}>
          {error}
        </div>
      )}

      {notice && (
        <div
          className={`alert alert-${notice.tone === 'warning' ? 'warning' : 'success'} py-2 mb-3 d-flex justify-content-between align-items-start`}
          style={{ fontSize: 13 }}
        >
          <span>{notice.text}</span>
          <button
            type='button'
            className='btn-close ms-2'
            style={{ fontSize: 10 }}
            aria-label={t('viab_notice_close')}
            onClick={() => setNotice(null)}
          />
        </div>
      )}

      {view === 'checklist' && (
        <>
          {/* Header del checklist + summary (las acciones por-ítem viven en el preview; la
              acción "Aprobar documentación" vive en el footer). El botón "Ver documentos
              recibidos" abre la vista pool cuando hay docs sin clasificar. */}
          <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
            <h5 className='fw-semibold mb-0' style={{ fontSize: 14 }}>
              {t('cl_title')}
            </h5>
            <div className='d-flex align-items-center gap-2 flex-wrap'>
              {Badge && (
                <Badge
                  text={`${(summary.recibidos ?? 0) + (summary.aprobados ?? 0)}/${(summary.total ?? items.length) - (summary.noAplica ?? 0)}`}
                  color={summary.isComplete ? 'success' : 'warning'}
                  size='sm'
                />
              )}
              {isApproved && Badge && (
                <Badge
                  text={t('doc_approved_badge')}
                  color='success'
                  size='sm'
                />
              )}
              {isCanceled && Badge && (
                <Badge text={t('term_badge')} color='error' size='sm' />
              )}
              {/* Siempre visible (incl. contador en 0): el pool se carga al montar. */}
              {Button && (
                <Button
                  label={`${t('cl_pool_open')} (${pool.length})`}
                  size='sm'
                  variant='naked'
                  onClick={() => setView('pool')}
                />
              )}
              {Button && (
                <Button
                  label={t('msgs_button')}
                  size='sm'
                  variant='naked'
                  onClick={() => setView('messages')}
                />
              )}
            </div>
          </div>

          {/* Tabla del checklist (revisión por ítem) */}
          <div className='table-container mb-3 na-reneg-checklist'>
            {Table ? (
              <Table columns={columns} data={items} loading={false} />
            ) : (
              <p className='font-light mb-0' style={{ color: '#94A3B8' }}>
                —
              </p>
            )}
            {items.length === 0 && (
              <div className='text-center p-3'>
                <p className='caption font-light mb-0'>{t('doc_empty')}</p>
              </div>
            )}
          </div>

          <div
            className='d-flex flex-column align-items-end gap-1 pt-3'
            style={{ borderTop: '1px solid #E2E8F0' }}
          >
            {!summary.isComplete && actionable && (
              <p
                className='caption font-light mb-0'
                style={{ color: '#94A3B8', width: '100%' }}
              >
                {t('doc_approve_all_hint')}
              </p>
            )}
            <div className='d-flex gap-2 align-items-center flex-wrap justify-content-end'>
              {canTerminate && Button && (
                <Button
                  label={t('term_button')}
                  size='sm'
                  variant='danger'
                  onClick={() => {
                    setNotice(null);
                    setError(null);
                    setCancelReason('');
                    setCancelComment('');
                    setCancelStatus('idle');
                    setCancelError(null);
                    setCancelOpen(true);
                  }}
                  disabled={busy || canceling}
                />
              )}
              {actionable && Button && (
                <Button
                  label={t('doc_approve_all')}
                  size='sm'
                  variant='primary'
                  onClick={() => {
                    setNotice(null);
                    setApproveStatus('idle');
                    setApproveError(null);
                    setApproveOpen(true);
                  }}
                  disabled={
                    busy ||
                    canceling ||
                    !summary.isComplete ||
                    isApproved ||
                    isCanceled
                  }
                />
              )}
            </div>
          </div>
        </>
      )}

      {view === 'pool' && (
        <>
          {/* Vista pool: volver al checklist + título */}
          <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
            <h5 className='fw-semibold mb-0' style={{ fontSize: 14 }}>
              {t('cl_pool_title')}
            </h5>
            {/* "Volver" con flecha: Button no tiene prop icon → IconButton + Button naked. */}
            <div className='d-flex align-items-center gap-1'>
              {IconButton && (
                <IconButton
                  icon='arrowLeft'
                  size='sm'
                  ariaLabel={t('cl_pool_back')}
                  onClick={() => setView('checklist')}
                />
              )}
              {Button && (
                <Button
                  label={t('cl_pool_back')}
                  size='sm'
                  variant='naked'
                  onClick={() => setView('checklist')}
                />
              )}
            </div>
          </div>

          {/* Tabla del pool (asignar / ver / descartar) */}
          <div className='table-container mb-3 na-eepp-grid-4'>
            {Table ? (
              <Table columns={poolColumns} data={pool} loading={poolLoading} />
            ) : null}
            {!poolLoading && pool.length === 0 && (
              <div className='text-center p-3'>
                <p className='caption font-light mb-0'>{t('cl_pool_empty')}</p>
              </div>
            )}
          </div>

          {/* Subir documento: se clasifica normal; si no matchea vuelve al pool */}
          <div className='row g-2 align-items-end mb-3'>
            <div className='col-md-8'>
              <label className='form-label caption mb-1'>
                {t('doc_file_label')}
              </label>
              <input
                ref={poolFileInputRef}
                type='file'
                accept='application/pdf,image/jpeg,image/png'
                className='form-control form-control-sm'
                onChange={(e) => setPoolFile(e.target.files?.[0] || null)}
              />
            </div>
            <div className='col-md-4'>
              {Button && (
                <Button
                  label={
                    poolUploading ? t('cl_pool_uploading') : t('cl_pool_upload')
                  }
                  size='sm'
                  variant='secondary'
                  onClick={handlePoolUpload}
                  disabled={poolUploading || !poolFile}
                />
              )}
            </div>
          </div>

          {/* Footer: guardar asignaciones (marca los ítems recibidos) */}
          <div
            className='d-flex justify-content-end gap-1 pt-3'
            style={{ borderTop: '1px solid #E2E8F0' }}
          >
            {Button && (
              <Button
                label={t('cl_pool_save')}
                size='sm'
                variant='primary'
                onClick={handleSaveAssignments}
                disabled={poolBusy || !hasAssignments}
              />
            )}
          </div>
        </>
      )}

      {view === 'messages' && (
        <>
          <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
            <h5 className='fw-semibold mb-0' style={{ fontSize: 14 }}>
              {t('msgs_title')}
            </h5>
            <div className='d-flex align-items-center gap-1'>
              {IconButton && (
                <IconButton
                  icon='arrowLeft'
                  size='sm'
                  ariaLabel={t('msgs_back')}
                  onClick={() => setView('checklist')}
                />
              )}
              {Button && (
                <Button
                  label={t('msgs_back')}
                  size='sm'
                  variant='naked'
                  onClick={() => setView('checklist')}
                />
              )}
            </div>
          </div>

          <div className='table-container mb-3 na-eepp-grid-5'>
            {Table ? (
              <Table columns={msgColumns} data={msgs} loading={msgsLoading} />
            ) : null}
            {!msgsLoading && msgs.length === 0 && (
              <div className='text-center p-3'>
                <p className='caption font-light mb-0'>{t('msgs_empty')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Preview del archivo recibido + acciones (Aprobar/Rechazar) en el footer:
          hay que ver el documento antes de decidir. */}
      {previewOpen && previewUrl && Modal && (
        <Modal
          title={previewItem?.documento || t('doc_view')}
          show
          onClose={closePreview}
          size='lg'
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
        >
          <iframe
            src={previewUrl}
            title={t('doc_view')}
            style={{ width: '100%', height: '65vh', border: 0 }}
          />

          {/* Rechazo: razón inline + confirmar/cancelar */}
          {rejecting ? (
            <div
              className='pt-3 mt-2'
              style={{ borderTop: '1px solid #E2E8F0' }}
            >
              <TextArea
                label={t('cl_reject_reason')}
                placeholder={t('cl_reject_reason_placeholder')}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target?.value ?? e)}
                rows={2}
                maxLength={4000}
              />
              <div className='d-flex justify-content-end gap-2 mt-2'>
                {Button && (
                  <Button
                    label={t('cl_reject_cancel')}
                    size='sm'
                    variant='naked'
                    onClick={() => {
                      setRejecting(false);
                      setRejectReason('');
                    }}
                    disabled={previewBusy}
                  />
                )}
                {Button && (
                  <Button
                    label={t('cl_reject_confirm')}
                    size='sm'
                    variant='danger'
                    onClick={() => handleRejectItem(previewItem)}
                    disabled={previewBusy}
                  />
                )}
              </div>
            </div>
          ) : (
            <div
              className='d-flex justify-content-end gap-2 pt-3 mt-2 flex-wrap'
              style={{ borderTop: '1px solid #E2E8F0' }}
            >
              {Button && (
                <Button
                  label={t('doc_download')}
                  size='sm'
                  variant='naked'
                  onClick={() => handleDownload(previewItem)}
                />
              )}
              {canReviewPreview && Button && (
                <>
                  <Button
                    label={t('cl_item_reject')}
                    size='sm'
                    variant='secondary'
                    onClick={() => {
                      setRejectReason('');
                      setRejecting(true);
                    }}
                    disabled={previewBusy}
                  />
                  <Button
                    label={t('cl_item_approve')}
                    size='sm'
                    variant='primary'
                    onClick={() => handleApproveItem(previewItem)}
                    disabled={previewBusy}
                  />
                </>
              )}
            </div>
          )}
        </Modal>
      )}

      {/* Reconciliación de duplicados (Fase 3): compara el archivo actual del ítem (ganador) con
          los candidatos que llegaron por otro canal. "Conservar este" acepta el elegido (reemplaza
          el archivo del ítem) y descarta los demás. */}
      {reconcileItem && Modal && (
        <Modal
          title={t('cl_reconcile_title')}
          show
          onClose={() => {
            if (!reconcileBusy) closeReconcile();
          }}
          size='lg'
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
        >
          <p style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
            {t('cl_reconcile_desc')}
          </p>
          <div className='d-flex gap-3 flex-wrap'>
            {reconcileDocs.map((d) => (
              <div
                key={d.id}
                style={{
                  flex: '1 1 320px',
                  minWidth: 280,
                  border: '1px solid #E2E8F0',
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <div className='d-flex align-items-center justify-content-between mb-2 gap-2'>
                  <div className='d-flex align-items-center gap-2'>
                    {sourceBadge(d.source, Badge, t)}
                    {d.isWinner && Badge && (
                      <Badge
                        text={t('cl_reconcile_current')}
                        color='neutral'
                        size='sm'
                      />
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 12,
                      color: '#64748B',
                      maxWidth: 140,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={d.fileName || ''}
                  >
                    {d.fileName || `doc-${d.id}`}
                  </span>
                </div>
                {d.url ? (
                  <iframe
                    src={d.url}
                    title={d.fileName || `doc-${d.id}`}
                    style={{
                      width: '100%',
                      height: '46vh',
                      border: 0,
                      background: '#F8FAFC',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      height: '46vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#94A3B8',
                      fontSize: 13,
                      background: '#F8FAFC',
                    }}
                  >
                    {t('cl_reconcile_no_preview')}
                  </div>
                )}
                <div className='d-flex justify-content-end mt-2'>
                  {Button && (
                    <Button
                      label={t('cl_reconcile_keep')}
                      size='sm'
                      variant='primary'
                      onClick={() => handleReconcileKeep(d.id)}
                      disabled={reconcileBusy}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {/* Confirmación de "Aprobar documentación" → WhatsApp de resolución al cliente.
          Modal propio + AsyncStateView: idle = pregunta + fila Cancelar/Aprobar; enviar →
          loader; éxito → check breve + autocierre; error → mensaje del back + "Volver". */}
      {approveOpen && Modal && (
        <Modal
          title={t('doc_approve_all_modal_title')}
          show
          onClose={() => {
            if (approveStatus === 'loading' || approveStatus === 'success')
              return;
            setApproveOpen(false);
            // Warning: la documentación YA quedó aprobada → avisamos al padre igual.
            if (approveStatus === 'warning') onApproved?.();
          }}
          size='md'
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
        >
          <AsyncStateView
            status={approveStatus}
            loadingTitle={t('doc_approve_state_loading')}
            successTitle={t('doc_approve_state_success')}
            warningTitle={t('doc_approve_state_success')}
            warningDescription={t('cl_warning_approved_not_notified')}
            errorMessage={approveError}
            // En warning la acción ya se aplicó: "Entendido" cierra y refresca; en error
            // "Volver" devuelve al formulario.
            onBack={() => {
              if (approveStatus !== 'warning') {
                setApproveStatus('idle');
                return;
              }
              setApproveOpen(false);
              onApproved?.();
            }}
            onSuccessAutoClose={() => {
              setApproveOpen(false);
              onApproved?.();
            }}
          >
            <div className='d-flex flex-column gap-3'>
              <p className='caption font-light mb-0'>
                {t('doc_approve_all_modal_message')}
              </p>
              <div className='d-flex justify-content-end gap-2'>
                {Button && (
                  <Button
                    label={t('viab_cancel')}
                    size='sm'
                    variant='secondary'
                    onClick={() => setApproveOpen(false)}
                  />
                )}
                {Button && (
                  <Button
                    label={t('doc_approve_all')}
                    size='sm'
                    variant='primary'
                    onClick={handleApproveDocumentation}
                  />
                )}
              </div>
            </div>
          </AsyncStateView>
        </Modal>
      )}

      {/* "Terminar proceso": razón (C2) + comentario (obligatorio si Otro) + aviso de la
          cascada de ítems sin disponer. Modal propio (Dropdown + TextArea + lista): el
          ConfirmModal solo admite un mensaje de texto. Confirmar → cancelProcess → la vista
          queda read-only con el badge "Terminado". El botón cancelar reusa la key genérica. */}
      {cancelOpen && Modal && (
        <Modal
          title={t('term_title')}
          show
          onClose={() => {
            if (
              canceling ||
              cancelStatus === 'loading' ||
              cancelStatus === 'success'
            )
              return;
            setCancelOpen(false);
            // Warning: el proceso YA quedó terminado → avisamos al padre igual.
            if (cancelStatus === 'warning') onChanged?.();
          }}
          size='md'
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
        >
          <AsyncStateView
            status={cancelStatus}
            loadingTitle={t('term_state_loading')}
            successTitle={t('term_state_success')}
            warningTitle={t('term_state_success')}
            warningDescription={t('cl_warning_canceled_not_notified')}
            errorMessage={cancelError}
            // En warning la acción ya se aplicó: "Entendido" cierra y refresca; en error
            // "Volver" devuelve al formulario.
            onBack={() => {
              if (cancelStatus !== 'warning') {
                setCancelStatus('idle');
                return;
              }
              setCancelOpen(false);
              onChanged?.();
            }}
            onSuccessAutoClose={() => {
              setCancelOpen(false);
              onChanged?.();
            }}
          >
            <div className='d-flex flex-column gap-3'>
              {Dropdown && (
                <div>
                  <label className='caption font-light d-block mb-1'>
                    {t('term_reason_label')}
                  </label>
                  <Dropdown
                    options={cancelReasonOptions}
                    value={
                      cancelReasonOptions.find(
                        (o) => o.value === cancelReason
                      ) ?? null
                    }
                    onChange={(opt) => setCancelReason(opt?.value ?? '')}
                    placeholder={t('term_reason_label')}
                    width='100%'
                  />
                </div>
              )}
              <TextArea
                label={t('term_comment_label')}
                value={cancelComment}
                onChange={(e) => setCancelComment(e.target?.value ?? e)}
                rows={3}
                maxLength={500}
                showCount
                disabled={canceling}
              />
              {cancelReason === 'other' &&
                cancelComment.trim().length === 0 && (
                  <p
                    className='caption mb-0'
                    style={{ color: 'var(--error-500)' }}
                  >
                    {t('term_comment_required')}
                  </p>
                )}
              {cascadeItems.length > 0 && (
                <div>
                  <p className='caption mb-1'>{t('term_cascade_warning')}</p>
                  <ul
                    className='mb-0'
                    style={{ fontSize: 13, color: '#475569', paddingLeft: 18 }}
                  >
                    {cascadeItems.map((i) => (
                      <li key={i.id}>{i.documento}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div
              className='d-flex justify-content-end gap-2 pt-3 mt-3'
              style={{ borderTop: '1px solid #E2E8F0' }}
            >
              {Button && (
                <Button
                  label={t('viab_cancel')}
                  size='sm'
                  variant='secondary'
                  onClick={() => setCancelOpen(false)}
                  disabled={canceling}
                />
              )}
              {Button && (
                <Button
                  label={t('term_confirm')}
                  size='sm'
                  variant='danger'
                  onClick={handleCancelProcess}
                  disabled={!canConfirmCancel || canceling}
                />
              )}
            </div>
          </AsyncStateView>
        </Modal>
      )}

      {/* Rechazar documento: modal (documento + motivo + toggle "volver a pedirlo"). Reemplaza el
          viejo ⋮ Rechazar que bloqueaba: el ítem vuelve a pendiente y (con toggle) se re-pide al
          cliente con el motivo. NO cancela/bloquea el proceso. */}
      {rejectTarget && Modal && (
        <Modal
          title={t('cl_reject_title')}
          show
          onClose={() => {
            if (rejectBusy) return;
            setRejectTarget(null);
            setRejectError(null);
          }}
          size='md'
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
        >
          <div className='d-flex flex-column gap-3'>
            <div>
              <label className='caption font-light d-block mb-1'>
                {t('cl_reject_doc_label')}
              </label>
              <p className='mb-0' style={{ fontSize: 14, fontWeight: 500 }}>
                {rejectTarget.documento}
              </p>
            </div>
            <TextArea
              label={t('cl_reject_reason')}
              placeholder={t('cl_reject_reason_placeholder')}
              value={rejectReasonInput}
              onChange={(e) => setRejectReasonInput(e.target?.value ?? e)}
              rows={3}
              maxLength={1000}
              showCount
              disabled={rejectBusy}
            />
            <label
              className='d-flex align-items-center gap-2 mb-0'
              style={{
                fontSize: 14,
                cursor: rejectBusy ? 'default' : 'pointer',
              }}
            >
              <input
                type='checkbox'
                checked={rejectReRequest}
                onChange={(e) => setRejectReRequest(e.target.checked)}
                disabled={rejectBusy}
              />
              {t('cl_reject_toggle')}
            </label>
            <p className='caption mb-0' style={{ color: '#64748B' }}>
              {rejectReRequest
                ? t('cl_reject_toggle_on_hint')
                : t('cl_reject_toggle_off_hint')}
            </p>
            {rejectError && (
              <div
                className='alert alert-danger py-2 mb-0'
                style={{ fontSize: 13 }}
              >
                {rejectError}
              </div>
            )}
          </div>
          <div
            className='d-flex justify-content-end gap-2 pt-3 mt-3'
            style={{ borderTop: '1px solid #E2E8F0' }}
          >
            {Button && (
              <Button
                label={t('viab_cancel')}
                size='sm'
                variant='secondary'
                onClick={() => setRejectTarget(null)}
                disabled={rejectBusy}
              />
            )}
            {Button && (
              <Button
                label={t('cl_reject_confirm')}
                size='sm'
                variant='danger'
                onClick={handleConfirmReject}
                disabled={rejectBusy || rejectReasonInput.trim().length === 0}
              />
            )}
          </div>
        </Modal>
      )}

      {/* Modal "Solicitar de nuevo": motivo obligatorio, que viaja al cliente junto al texto
          configurado en la regla. En modo discard_rerequest además se descarta el documento
          ya recibido, así que se avisa. */}
      {rerequestTarget && Modal && (
        <Modal
          title={t('cl_rerequest_title')}
          show
          onClose={() => {
            if (rerequestBusy) return;
            setRerequestTarget(null);
            setRerequestError(null);
          }}
          size='md'
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
        >
          <div className='d-flex flex-column gap-3'>
            <div>
              <label className='caption font-light d-block mb-1'>
                {t('cl_reject_doc_label')}
              </label>
              <p className='mb-0' style={{ fontSize: 14, fontWeight: 500 }}>
                {rerequestTarget.item.documento}
              </p>
            </div>
            <TextArea
              label={t('cl_rerequest_reason')}
              placeholder={t('cl_rerequest_reason_placeholder')}
              value={rerequestReasonInput}
              onChange={(e) => setRerequestReasonInput(e.target?.value ?? e)}
              rows={3}
              maxLength={1000}
              showCount
              disabled={rerequestBusy}
            />
            <p className='caption mb-0' style={{ color: '#64748B' }}>
              {rerequestTarget.mode === 'discard_rerequest'
                ? t('cl_rerequest_hint_discard')
                : t('cl_rerequest_hint')}
            </p>
            {rerequestError && (
              <div
                className='alert alert-danger py-2 mb-0'
                style={{ fontSize: 13 }}
              >
                {rerequestError}
              </div>
            )}
          </div>
          <div
            className='d-flex justify-content-end gap-2 pt-3 mt-3'
            style={{ borderTop: '1px solid #E2E8F0' }}
          >
            {Button && (
              <Button
                label={t('viab_cancel')}
                size='sm'
                variant='secondary'
                onClick={() => setRerequestTarget(null)}
                disabled={rerequestBusy}
              />
            )}
            {Button && (
              <Button
                label={t('cl_rerequest_confirm')}
                size='sm'
                variant='primary'
                onClick={handleConfirmReRequest}
                disabled={
                  rerequestBusy || rerequestReasonInput.trim().length === 0
                }
              />
            )}
          </div>
        </Modal>
      )}

      {/* Preview del doc del pool: PROPIO y solo lectura (footer solo Descargar) */}
      {poolPreview && Modal && (
        <Modal
          title={poolPreview.doc?.fileName || t('doc_view')}
          show
          onClose={() => setPoolPreview(null)}
          size='lg'
          primaryBtnTxt={null}
          secondaryBtnTxt={null}
        >
          <iframe
            src={poolPreview.url}
            title={t('doc_view')}
            style={{ width: '100%', height: '65vh', border: 0 }}
          />
          <div
            className='d-flex justify-content-end gap-2 pt-3 mt-2'
            style={{ borderTop: '1px solid #E2E8F0' }}
          >
            {Button && (
              <Button
                label={t('doc_download')}
                size='sm'
                variant='naked'
                onClick={() => handlePoolDownload(poolPreview.doc)}
              />
            )}
          </div>
        </Modal>
      )}

      {/* Descartar documento del pool: silencioso (sin WhatsApp al cliente) */}
      {discardTarget && (
        <ConfirmModal
          Modal={Modal}
          show
          title={t('cl_pool_discard_title')}
          message={t('cl_pool_discard_message')}
          confirmLabel={t('cl_pool_discard')}
          cancelLabel={t('viab_cancel')}
          confirmVariant='danger'
          confirmDisabled={poolBusy}
          onConfirm={handleDiscard}
          onClose={() => setDiscardTarget(null)}
        />
      )}

      {/* Descarte silencioso del ⋮ (recibido): el ítem vuelve a pendiente sin avisar al cliente.
          El descarte CON re-pedido ya no pasa por acá: pide motivo en su propio modal. */}
      {discardItemTarget && (
        <ConfirmModal
          Modal={Modal}
          show
          title={t('cl_item_discard_forget_title')}
          message={t('cl_item_discard_forget_message')}
          confirmLabel={t('cl_item_discard_forget')}
          cancelLabel={t('viab_cancel')}
          confirmVariant='danger'
          confirmDisabled={itemActionBusy}
          onConfirm={() => handleDiscardForget(discardItemTarget.item)}
          onClose={() => setDiscardItemTarget(null)}
        />
      )}
    </div>
  );
}
